/**
 * What an AI tool surface does when the server refuses a text for length
 * (400 text_too_long).
 *
 * The browser's ceilings come from the tenant config, cached for ten
 * minutes. When the owner lowers one, a page opened before the change is
 * refused at the new number while its boxes still hold the old one. On
 * that refusal, and only on it, the tenant config is read again, so the
 * box's note and button catch up with no further typing. The number the
 * sentence says is the server's, returned here, until they do.
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { tenantConfigQueryKey } from '@/hooks/useTenantConfig';
import { isTextTooLongRefusal, serverMaxCharsOf } from '@/lib/errorCopy';

/** Returns a handler for a failed AI request: it refreshes the tenant
 *  config on a refusal for length and gives back the server's ceiling
 *  (null when the failure carries none). */
export function useInvalidateOnCeilingRefusal(tenantSlug: string): (e: unknown) => number | null {
  const queryClient = useQueryClient();
  return useCallback(
    (e: unknown) => {
      if (isTextTooLongRefusal(e)) {
        void queryClient.invalidateQueries({ queryKey: tenantConfigQueryKey(tenantSlug) });
      }
      return serverMaxCharsOf(e);
    },
    [queryClient, tenantSlug],
  );
}
